import {useState} from 'react';
import {AlertCircle, Calendar, Check, Copy, CreditCard, KeyRound, Package} from 'lucide-react';
import {Alert, CircularProgress, Dialog, DialogContent, DialogTitle} from '@mui/material';
import {PortalShell} from '../../shared/ui';
import {getPlanoLicenciamentoById} from './LicencasService';
import {getBackendMessage, normalizePlanoLicenciamento} from './licencasUtils';
import {useLicencas} from './useLicencas';
import {useOpcionaisContratados} from './useOpcionaisContratados';
import OpcionaisContratados from './OpcionaisContratados';

const statusConfig = {
    ATIVA: {label: 'Ativa', className: 'bf-pill bf-pill--success'},
    INATIVA: {label: 'Inativa', className: 'bf-pill bf-pill--muted'},
    BLOQUEADA: {label: 'Bloqueada', className: 'bf-pill bf-pill--danger'},
    CANCELADA: {label: 'Cancelada', className: 'bf-pill bf-pill--muted'},
    TRIAL: {label: 'Trial', className: 'bf-pill bf-pill--warning'},
    ATIVO: {label: 'Ativo', className: 'bf-pill bf-pill--success'},
};

function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'});
}

function formatDateTime(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleString('pt-BR');
}

function getDaysUntilFromBR(dateString) {
    if (!dateString || dateString === '-') return 0;
    const [day, month, year] = dateString.split('/');
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function formatMoneyBR(value) {
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(value || 0));
}

export default function LicencasPage() {
    const data = useLicencas();
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [planError, setPlanError] = useState('');
    const [copiedKey, setCopiedKey] = useState(null);
    const opcionais = useOpcionaisContratados(selectedLicense ? [selectedLicense.id] : []);

    const totalLicencas = data.rows.length;
    const licencasAtivas = data.rows.filter((item) => item.raw?.status === 'ATIVA').length;
    const proximoVencimento = data.rows
        .filter((item) => item.raw?.validade_fim)
        .sort((a, b) => new Date(a.raw.validade_fim).getTime() - new Date(b.raw.validade_fim).getTime())[0];

    const handleCopyKey = async (key) => {
        await navigator.clipboard.writeText(key);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleOpenDetails = async (license) => {
        setSelectedLicense(license);
        setSelectedPlan(null);
        setPlanError('');
        const planId = license?.raw?.plano?.id || license?.raw?.id_plano || license?.raw?.plano_id;
        if (!planId) {
            setPlanError('Plano de licenciamento não encontrado para esta licença.');
            return;
        }
        try {
            setLoadingPlan(true);
            const response = await getPlanoLicenciamentoById(planId);
            const payload = response?.data?.valor ?? response?.data?.data ?? response?.data ?? {};
            setSelectedPlan(normalizePlanoLicenciamento(payload));
        } catch (error) {
            setPlanError(getBackendMessage(error, 'Não foi possível carregar os detalhes do plano.'));
        } finally {
            setLoadingPlan(false);
        }
    };

    return (
        <PortalShell>
            <div className="bf-page">
                <div>
                    <h1 className="bf-page__title">Licenças</h1>
                    <p className="bf-page__subtitle">Acompanhe suas licenças, validades e informações de assinatura.</p>
                </div>

                {data.error ? <Alert severity="warning">{data.error}</Alert> : null}

                {data.loading ? (
                    <div className="bf-empty"><CircularProgress /></div>
                ) : (
                    <>
                        <div className="bf-grid-3">
                            <div className="bf-card"><div className="bf-card__body"><div className="bf-row" style={{gap: 12, marginBottom: 12}}><div style={{color: 'var(--bf-primary)'}}><KeyRound size={20} /></div><span className="bf-muted" style={{fontSize: 14}}>Total de Licenças</span></div><div className="bf-kpi-value">{totalLicencas}</div></div></div>
                            <div className="bf-card"><div className="bf-card__body"><div className="bf-row" style={{gap: 12, marginBottom: 12}}><div style={{color: 'var(--bf-success)'}}><Package size={20} /></div><span className="bf-muted" style={{fontSize: 14}}>Licenças Ativas</span></div><div className="bf-kpi-value">{licencasAtivas}</div></div></div>
                            <div className="bf-card"><div className="bf-card__body"><div className="bf-row" style={{gap: 12, marginBottom: 12}}><div style={{color: 'var(--bf-warning)'}}><Calendar size={20} /></div><span className="bf-muted" style={{fontSize: 14}}>Próximo Vencimento</span></div><div className="bf-kpi-value">{proximoVencimento ? `${Math.max(Math.ceil((new Date(proximoVencimento.raw.validade_fim).getTime() - Date.now()) / (1000*60*60*24)), 0)} dias` : '-'}</div></div></div>
                        </div>

                        <div className="bf-card"><div className="bf-card__body bf-table-wrap">
                            <table className="bf-table">
                                <thead>
                                    <tr>
                                        <th>Sistema</th>
                                        <th>Plano</th>
                                        <th>Status</th>
                                        <th>Tipo</th>
                                        <th>Validade</th>
                                        <th>Chave de Ativação</th>
                                        <th style={{textAlign: 'right'}}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((license) => {
                                        const raw = license.raw || {};
                                        const daysUntilExpiry = getDaysUntilFromBR(license.expiry);
                                        const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                                        const status = statusConfig[raw?.status] || statusConfig.ATIVA;
                                        return (
                                            <tr key={license.id}>
                                                <td style={{fontWeight: 500}}>{license.system}</td>
                                                <td><span className="bf-pill bf-pill--muted">{license.plan}</span></td>
                                                <td><span className={status.className}>{status.label}</span></td>
                                                <td className="bf-muted">{raw?.tipo || '-'}</td>
                                                <td>
                                                    <div className="bf-row" style={{gap: 8}}>
                                                        {isExpiringSoon ? <AlertCircle size={14} color="#f59e0b" /> : null}
                                                        <span style={isExpiringSoon ? {color: 'var(--bf-warning)', fontWeight: 500} : null}>{formatDate(raw?.validade_fim)}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="bf-row" style={{gap: 8}}>
                                                        <code className="bf-code">{license.name}</code>
                                                        <button className="bf-icon-button" onClick={() => handleCopyKey(license.name)} title="Copiar chave">
                                                            {copiedKey === license.name ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td style={{textAlign: 'right'}}>
                                                    <button className="bf-button bf-button--ghost bf-button--sm" onClick={() => handleOpenDetails(license)}>Ver Detalhes</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div></div>
                    </>
                )}
            </div>

            <Dialog open={Boolean(selectedLicense)} onClose={() => { setSelectedLicense(null); setSelectedPlan(null); setPlanError(''); }} fullWidth maxWidth="lg">
                <DialogTitle>{selectedLicense ? `Detalhes da Licença - ${selectedLicense.system}` : 'Detalhes da Licença'}</DialogTitle>
                <DialogContent>
                    {selectedLicense ? (
                        <div className="bf-stack-3" style={{paddingTop: 8, paddingBottom: 8}}>
                            {loadingPlan ? <div className="bf-empty"><CircularProgress /></div> : null}
                            {planError ? <Alert severity="warning">{planError}</Alert> : null}

                            <div className="bf-modal-grid">
                                <div><div className="bf-meta-label">ID da Licença</div><div className="bf-meta-value bf-code">{selectedLicense.raw?.id || '-'}</div></div>
                                <div><div className="bf-meta-label">Tenant ID</div><div className="bf-meta-value bf-code">{selectedLicense.tenantId}</div></div>
                                <div><div className="bf-meta-label">Plano</div><div className="bf-meta-value">{selectedPlan?.name || selectedLicense.plan}</div></div>
                                <div><div className="bf-meta-label">Status</div><div className="bf-meta-value"><span className={(statusConfig[selectedPlan?.status] || statusConfig[selectedLicense.raw?.status] || statusConfig.ATIVA).className}>{(statusConfig[selectedPlan?.status] || statusConfig[selectedLicense.raw?.status] || {label: selectedPlan?.status || selectedLicense.raw?.status || '-'}).label}</span></div></div>
                            </div>

                            <div className="bf-modal-section">
                                <h4 className="bf-row" style={{gap: 8, margin: '0 0 16px'}}><Calendar size={18} />Datas e Validade</h4>
                                <div className="bf-modal-grid">
                                    <div><div className="bf-meta-label">Início</div><div className="bf-meta-value">{formatDate(selectedLicense.raw?.validade_inicio)}</div></div>
                                    <div><div className="bf-meta-label">Fim</div><div className="bf-meta-value">{formatDate(selectedLicense.raw?.validade_fim)}</div></div>
                                    {selectedLicense.raw?.data_ultima_cobranca ? <div><div className="bf-meta-label">Última Cobrança</div><div className="bf-meta-value">{formatDate(selectedLicense.raw?.data_ultima_cobranca)}</div></div> : null}
                                    {selectedLicense.raw?.data_proxima_cobranca ? <div><div className="bf-meta-label">Próxima Cobrança</div><div className="bf-meta-value">{formatDate(selectedLicense.raw?.data_proxima_cobranca)}</div></div> : null}
                                </div>
                            </div>

                            <div className="bf-modal-section">
                                <h4 className="bf-row" style={{gap: 8, margin: '0 0 16px'}}><Package size={18} />Plano de Licenciamento</h4>
                                <div className="bf-modal-grid">
                                    <div><div className="bf-meta-label">Sistema</div><div className="bf-meta-value">{selectedPlan?.systemName || selectedLicense.system}</div></div>
                                    <div><div className="bf-meta-label">Uso Interno</div><div className="bf-meta-value">{selectedPlan ? (selectedPlan.internalUse ? 'Sim' : 'Não') : '-'}</div></div>
                                    <div><div className="bf-meta-label">Tipo de Cobrança</div><div className="bf-meta-value">{selectedPlan?.billingType || '-'}</div></div>
                                    <div><div className="bf-meta-label">Preço Base Mensal</div><div className="bf-meta-value">{formatMoneyBR(selectedPlan?.basePrice)}</div></div>
                                    <div><div className="bf-meta-label">Suporte</div><div className="bf-meta-value">{selectedPlan ? (selectedPlan.supportIncluded ? 'Incluso' : 'Não incluso') : '-'}</div></div>
                                    <div><div className="bf-meta-label">Add-ons</div><div className="bf-meta-value">{selectedPlan ? (selectedPlan.addonsAllowed ? 'Permitidos' : 'Não permitidos') : '-'}</div></div>
                                    <div><div className="bf-meta-label">Descrição</div><div className="bf-meta-value">{selectedPlan?.description || '-'}</div></div>
                                </div>
                            </div>

                            <div className="bf-modal-section">
                                <h4 className="bf-row" style={{gap: 8, margin: '0 0 16px'}}><Package size={18} />Recursos e Módulos</h4>
                                <div className="bf-stack-2">
                                    <div>
                                        <div className="bf-meta-label" style={{marginBottom: 8}}>Módulos Contratados</div>
                                        <div className="bf-stack-1">
                                            {(selectedPlan?.modules || []).map((module, index) => {
                                                const label = typeof module === 'string' ? module : module?.name || module?.modulo?.nome || module?.nome || module?.descricao || `Módulo ${index + 1}`;
                                                return <span key={`${label}-${index}`} className="bf-pill bf-pill--primary">{label}</span>;
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="bf-meta-label" style={{marginBottom: 8}}>Opcionais Contratados</div>
                                        <OpcionaisContratados estado={opcionais[selectedLicense.id]} />
                                    </div>
                                </div>
                            </div>

                            <div className="bf-modal-section">
                                <h4 className="bf-row" style={{gap: 8, margin: '0 0 16px'}}><CreditCard size={18} />Informações do Plano</h4>
                                <div className="bf-modal-grid">
                                    <div><div className="bf-meta-label">ID do Plano</div><div className="bf-meta-value bf-code">{selectedPlan?.id || '-'}</div></div>
                                    <div><div className="bf-meta-label">ID do Sistema</div><div className="bf-meta-value bf-code">{selectedPlan?.systemId || '-'}</div></div>
                                    <div><div className="bf-meta-label">Criado em</div><div className="bf-meta-value">{formatDateTime(selectedPlan?.createdAt || selectedLicense.raw?.criado_em)}</div></div>
                                    <div><div className="bf-meta-label">Atualizado em</div><div className="bf-meta-value">{formatDateTime(selectedPlan?.updatedAt || selectedLicense.raw?.atualizado_em)}</div></div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </PortalShell>
    );
}
